import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Camera,
  FileUp,
  LogOut,
  Phone,
  Plus,
  Search,
  Send,
  Settings,
  Users,
  Video,
  X,
} from "lucide-react";
import { isConfigured, supabase } from "../lib/supabase";
import type { Conversation, Message, Profile } from "../types";
import { makeClientId, mergeMessage } from "../services/messages";
import { CallHost } from "../features/calls/CallHost";
import { VoiceRecorder } from "../features/messages/VoiceRecorder";

const letters = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .map((v) => v[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "A";
type Person = Pick<Profile, "id" | "full_name" | "username" | "status">;

function Auth() {
  const nav = useNavigate();
  const [mode, setMode] = useState<"login" | "register" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setNotice("");
    try {
      if (!isConfigured) throw new Error("Supabase has not been configured.");
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        nav("/app");
        return;
      }
      if (mode === "register") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name } },
        });
        if (error) throw error;
        if (data.session) {
          nav("/app");
          return;
        }
        setNotice("Account created. Please sign in.");
        setMode("login");
        return;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: location.origin,
      });
      if (error) throw error;
      setNotice("Reset email sent.");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Authentication failed";
      setNotice(
        message.toLowerCase().includes("rate")
          ? "Too many email requests. Wait a minute and try again."
          : message,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth">
      <section>
        <div className="brandmark">M</div>
        <h1>AMI CONNECT</h1>
        <p>Connect. Chat. Stay Close.</p>
      </section>
      <form onSubmit={submit}>
        <h2>
          {mode === "login"
            ? "Welcome back"
            : mode === "register"
              ? "Create an account"
              : "Reset password"}
        </h2>
        {mode === "register" && (
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
          />
        )}
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
        />
        {mode !== "reset" && (
          <input
            required
            minLength={8}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
          />
        )}
        <button disabled={busy}>
          {busy
            ? "Please wait…"
            : mode === "login"
              ? "Sign in"
              : mode === "register"
                ? "Create account"
                : "Send reset link"}
        </button>
        {notice && <p className="error">{notice}</p>}
        <nav>
          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
          >
            {mode === "login" ? "Create account" : "Sign in"}
          </button>
          <button type="button" onClick={() => setMode("reset")}>
            Forgot password?
          </button>
        </nav>
      </form>
    </main>
  );
}

function Shell() {
  const [user, setUser] = useState<string | null>(null);
  useEffect(() => {
    void supabase.auth
      .getUser()
      .then(({ data }) => setUser(data.user?.id ?? null));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => setUser(s?.user.id ?? null));
    return () => subscription.unsubscribe();
  }, []);
  return user ? (
    <>
      <Workspace id={user} />
      <CallHost userId={user} />
    </>
  ) : (
    <Auth />
  );
}

function Workspace({ id }: { id: string }) {
  const nav = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [chats, setChats] = useState<Conversation[]>([]);
  const [search, setSearch] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const load = async () => {
    const [{ data: p }, { data: m }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", id).single(),
      supabase
        .from("conversation_members")
        .select("conversation:conversations(*)")
        .eq("user_id", id),
    ]);
    setProfile(p as Profile);
    setChats(
      (m ?? [])
        .map(
          (x) => (x as unknown as { conversation: Conversation }).conversation,
        )
        .filter(Boolean)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    );
  };
  useEffect(() => {
    void load();
  }, [id]);
  useEffect(() => {
    const show = (event: Event) =>
      setNotice(String((event as CustomEvent).detail));
    window.addEventListener("ami:notice", show);
    return () => window.removeEventListener("ami:notice", show);
  }, []);
  useEffect(() => {
    const run = async () => {
      const base = supabase
        .from("profiles")
        .select("id,full_name,username,status")
        .neq("id", id)
        .limit(20);
      const term = search.trim();
      const { data } = term
        ? await base.or(`full_name.ilike.%${term}%,username.ilike.%${term}%`)
        : await base.order("created_at", { ascending: false });
      setPeople((data ?? []) as Person[]);
    };
    void run();
  }, [search, id]);
  const direct = async (uid: string) => {
    const { data, error } = await supabase.rpc("create_direct_conversation", {
      other_id: uid,
    });
    if (error) {
      setNotice(error.message);
      return;
    }
    setOpen(false);
    await load();
    nav(`/app/chat/${data as string}`);
  };
  const createGroup = async () => {
    const { data, error } = await supabase.rpc("create_group_conversation", {
      group_title: groupName,
      member_ids: picked,
    });
    if (error) {
      setNotice(error.message);
      return;
    }
    setGroup(false);
    setPicked([]);
    setGroupName("");
    await load();
    nav(`/app/chat/${data as string}`);
  };
  return (
    <div className="shell">
      <aside>
        <header>
          <div className="avatar">{letters(profile?.full_name ?? "AMI")}</div>
          <div>
            <b>AMI CONNECT</b>
            <small>{profile?.status ?? "Offline"}</small>
          </div>
          <button aria-label="Settings">
            <Settings />
          </button>
          <button
            aria-label="Log out"
            onClick={() => void supabase.auth.signOut()}
          >
            <LogOut />
          </button>
        </header>
        <div className="search">
          <Search />
          <input
            value={search}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            placeholder="Search people"
          />
        </div>
        <div className="actions">
          <button className="new" onClick={() => setOpen(true)}>
            <Plus /> New chat
          </button>
          <button
            className="iconAction"
            onClick={() => {
              setGroup(true);
              setOpen(true);
            }}
          >
            <Users />
          </button>
        </div>
        <nav className="tabs">
          <span>Chats</span>
          <span>
            <Phone /> Calls
          </span>
        </nav>
        <div className="conversations">
          {chats.map((c) => (
            <button
              className="conversation"
              key={c.id}
              onClick={() => nav(`/app/chat/${c.id}`)}
            >
              <div className="avatar">
                {letters(c.title ?? (c.type === "group" ? "Group" : "Chat"))}
              </div>
              <span>
                <b>{c.title ?? "Direct message"}</b>
                <small>
                  {c.type === "group" ? "Group chat" : "Private chat"}
                </small>
              </span>
            </button>
          ))}
          {!chats.length && (
            <p className="muted">
              Start a conversation with someone on AMI CONNECT.
            </p>
          )}
        </div>
      </aside>
      <main className="content">
        <Routes>
          <Route
            path="chat/:cid"
            element={<Chat userId={id} onChanged={load} onNotice={setNotice} />}
          />
          <Route
            path="*"
            element={
              <section className="welcome">
                <div className="brandmark">M</div>
                <h1>AMI CONNECT</h1>
                <p>Choose a chat or find someone to start talking.</p>
              </section>
            }
          />
        </Routes>
        {notice && (
          <button className="toast" onClick={() => setNotice("")}>
            {notice}
            <X />
          </button>
        )}
      </main>
      {open && (
        <div className="modalBack">
          <section className="modal">
            <header>
              <h2>{group ? "Create group" : "New chat"}</h2>
              <button
                onClick={() => {
                  setOpen(false);
                  setGroup(false);
                }}
              >
                <X />
              </button>
            </header>
            {group && (
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Group name"
              />
            )}
            <div className="search">
              <Search />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search registered users"
              />
            </div>
            <div className="people">
              {people.map((p) => (
                <button
                  key={p.id}
                  onClick={() =>
                    group
                      ? setPicked((v) =>
                          v.includes(p.id)
                            ? v.filter((x) => x !== p.id)
                            : [...v, p.id],
                        )
                      : void direct(p.id)
                  }
                >
                  <div className="avatar">{letters(p.full_name)}</div>
                  <span>
                    <b>{p.full_name || p.username || "User"}</b>
                    <small>
                      @{p.username || "new"} · {p.status}
                    </small>
                  </span>
                  {group && (
                    <input
                      type="checkbox"
                      readOnly
                      checked={picked.includes(p.id)}
                    />
                  )}
                </button>
              ))}
              {!people.length && (
                <p className="muted">
                  No other registered users are available yet. Create a second
                  account in another browser, then return here to start a chat.
                </p>
              )}
            </div>
            {group && (
              <button
                className="new"
                disabled={!groupName.trim() || !picked.length}
                onClick={createGroup}
              >
                Create group ({picked.length})
              </button>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Chat({
  userId,
  onChanged,
  onNotice,
}: {
  userId: string;
  onChanged: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const { cid = "" } = useParams();
  const [chat, setChat] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const load = async () => {
      const [{ data: c }, { data: m }] = await Promise.all([
        supabase.from("conversations").select("*").eq("id", cid).single(),
        supabase
          .from("messages")
          .select("*, sender:profiles!messages_sender_id_fkey(*)")
          .eq("conversation_id", cid)
          .order("created_at", { ascending: true })
          .limit(100),
      ]);
      setChat(c as Conversation);
      setMessages((m ?? []) as Message[]);
    };
    void load();
    const channel = supabase
      .channel(`chat:${cid}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${cid}`,
        },
        (event) => setMessages((v) => mergeMessage(v, event.new as Message)),
      )
      .on("broadcast", { event: "typing" }, ({ payload }) => {
        if (payload.userId !== userId) {
          setTyping(true);
          setTimeout(() => setTyping(false), 2200);
        }
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [cid, userId]);
  useEffect(
    () => bottom.current?.scrollIntoView({ behavior: "smooth" }),
    [messages],
  );
  const deliver = async (
    content: string,
    attachmentId?: string,
    type: Message["message_type"] = "text",
  ) => {
    if (!content && !attachmentId) return;
    setText("");
    const client = makeClientId();
    setMessages((v) => [
      ...v,
      {
        id: `temp-${client}`,
        client_message_id: client,
        conversation_id: cid,
        sender_id: userId,
        message_type: type,
        content: content || "Attachment",
        created_at: new Date().toISOString(),
        edited_at: null,
        deleted_at: null,
        status: "sending",
      },
    ]);
    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: cid,
        client_message_id: client,
        message_type: type,
        content: content || null,
        attachment_id: attachmentId ?? null,
      })
      .select()
      .single();
    if (error) {
      setMessages((v) =>
        v.map((m) =>
          m.client_message_id === client ? { ...m, status: "failed" } : m,
        ),
      );
      onNotice(error.message);
      return;
    }
    setMessages((v) => mergeMessage(v, data as Message));
    void onChanged();
  };
  const attach = async (f: File) => {
    if (f.size > 50 * 1024 * 1024) {
      onNotice("Maximum file size is 50 MB.");
      return;
    }
    setUploading(true);
    try {
      const fileId = crypto.randomUUID();
      const bucket =
        f.type.startsWith("image/") || f.type.startsWith("video/")
          ? "chat-media"
          : f.type.startsWith("audio/")
            ? "voice-notes"
            : "documents";
      const path = `${userId}/${cid}/${fileId}-${f.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error: up } = await supabase.storage.from(bucket).upload(path, f);
      if (up) throw up;
      const { data, error } = await supabase
        .from("attachments")
        .insert({
          conversation_id: cid,
          owner_id: userId,
          bucket,
          path,
          mime_type: f.type,
          size_bytes: f.size,
        })
        .select()
        .single();
      if (error) throw error;
      await deliver(
        f.name,
        data.id,
        f.type.startsWith("image/")
          ? "image"
          : f.type.startsWith("audio/")
            ? "voice"
            : "file",
      );
    } catch (e) {
      onNotice(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const call = (video: boolean) => {
    window.dispatchEvent(
      new CustomEvent("ami:start-call", {
        detail: { conversationId: cid, video },
      }),
    );
  };
  return (
    <section className="chat">
      <header className="chathead">
        <div className="avatar">{letters(chat?.title ?? "Chat")}</div>
        <div>
          <b>{chat?.title ?? "Private conversation"}</b>
          <small>
            {typing
              ? "typing…"
              : chat?.type === "group"
                ? "Group chat"
                : "Online when available"}
          </small>
        </div>
        <div className="spacer" />
        <button onClick={() => void call(false)} aria-label="Audio call">
          <Phone />
        </button>
        <button onClick={() => void call(true)} aria-label="Video call">
          <Video />
        </button>
      </header>
      <div className="messages">
        {messages.map((m) => (
          <article key={m.id} className={m.sender_id === userId ? "mine" : ""}>
            <p>{m.deleted_at ? "This message was deleted." : m.content}</p>
            <small>
              {new Date(m.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              {m.status === "sending"
                ? " · sending"
                : m.status === "failed"
                  ? " · failed"
                  : ""}
            </small>
          </article>
        ))}
        <div ref={bottom} />
      </div>
      <footer>
        <input
          ref={file}
          className="fileInput"
          type="file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void attach(f);
            e.currentTarget.value = "";
          }}
        />
        <button onClick={() => file.current?.click()} aria-label="Attach file">
          <FileUp />
        </button>
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            void supabase.channel(`chat:${cid}`).send({
              type: "broadcast",
              event: "typing",
              payload: { userId },
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void deliver(text.trim());
            }
          }}
          placeholder="Write a message"
        />
        <button
          className="round"
          onClick={() => void deliver(text.trim())}
          aria-label="Send"
        >
          <Send />
        </button>
        <VoiceRecorder
          disabled={uploading}
          onSend={attach}
          onError={onNotice}
        />
        <button aria-label="Camera">
          <Camera />
        </button>
      </footer>
    </section>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/app/*" element={<Shell />} />
      <Route path="/" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
